import { useEffect, useState } from "react";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { Navigate } from "react-router-dom";
import { Plus, Edit2, Pause, Trash2, RefreshCw, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { KAFFA } from "@/constants/testIds";
import { MarketTicker } from "@/components/brand/MarketTicker";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { SampleManageDialog } from "@/components/brand/SampleManageDialog";

const COFFEE_TYPES = ["Arábica", "Conilon", "Robusta", "Especial", "Microlote", "Gourmet"];
const PROCESSES = ["Natural", "Washed", "Honey", "Pulped Natural", "Anaerobic", "Fermentado"];
const QUALITIES = ["Commercial", "Premium", "Specialty", "Microlot"];
const SIEVES = ["13/14", "15/16", "16/17", "17/18", "18/19", "19/20"];

const blankLot = {
  name: "", coffee_type: "Arábica", bags_quantity: 100, price_per_bag: 1200, currency: "BRL",
  quality_grade: "Premium", sca_score: 84, blend: "", sieve: "16/17", altitude_m: 1100,
  process: "Natural", crop_year: "2024/2025", humidity_pct: 11.5, region: "", state: "",
  city: "", farm_name: "", certifications: [], images: [], documents: [],
  auto_pricing_enabled: false, margin_pct: 8, fob_port: "Santos", available_for_export: true,
  status: "published", notes: "",
};

export default function CooperativeDashboardPage() {
  const { user } = useAuth();
  const { formatPrice, formatDate, t, tData } = useLocale();
  const [lots, setLots] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [interests, setInterests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankLot);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("lots");
  const [manageSample, setManageSample] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  const reload = async () => {
    try {
      const [lr, pr, ir] = await Promise.all([
        api.get("/lots"),
        api.get("/proposals"),
        api.get("/interests"),
      ]);
      setLots(lr.data); setProposals(pr.data); setInterests(ir.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (user && user.role === "cooperative") reload(); }, [user]);

  if (user === false) return <Navigate to="/login" />;
  if (!user) return null;
  if (user.role !== "cooperative") return <Navigate to="/" />;

  const startNew = () => { setEditingId(null); setForm(blankLot); setOpen(true); };
  const startEdit = (lot) => {
    setEditingId(lot.id);
    setForm({
      ...blankLot, ...lot,
      certifications: lot.certifications || [], images: lot.images || [], documents: lot.documents || [],
    });
    setOpen(true);
  };

  const submitLot = async () => {
    try {
      if (editingId) await api.patch(`/lots/${editingId}`, form);
      else await api.post("/lots", form);
      toast.success(editingId ? t("coop.toast.lot_updated") : t("coop.toast.lot_published"));
      setOpen(false); reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteLot = async (id) => {
    if (!confirm(t("coop.confirm_delete"))) return;
    await api.delete(`/lots/${id}`);
    toast.success(t("coop.toast.lot_removed")); reload();
  };
  const pauseLot = async (lot) => {
    await api.patch(`/lots/${lot.id}`, { status: lot.status === "paused" ? "published" : "paused" });
    toast.success(t("coop.toast.status_updated")); reload();
  };
  const recalc = async (id) => {
    try { await api.post(`/lots/${id}/recalculate`); toast.success(t("coop.toast.price_recalc")); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const respondProposal = async (id, status, counterPrice) => {
    try {
      await api.patch(`/proposals/${id}`, { status, counter_price: counterPrice });
      toast.success(t("coop.toast.response_sent")); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <MarketTicker />
      <main className="flex-1 mx-auto max-w-[1600px] w-full px-4 sm:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-3">
              {t("coop.desk")} · {user.name}
            </div>
            <h1 className="font-display text-4xl tracking-tighter font-black">{t("coop.title")}</h1>
          </div>
          <Button
            data-testid={KAFFA.coopCreateLot}
            onClick={startNew}
            className="h-11 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em]"
          >
            <Plus className="h-4 w-4 mr-2" /> {t("coop.new_lot")}
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border mb-8">
          <Kpi label={t("coop.kpi_active")} value={lots.filter((l) => l.status === "published").length} />
          <Kpi label={t("coop.kpi_bags")} value={lots.reduce((s, l) => s + (l.bags_quantity || 0), 0).toLocaleString()} />
          <Kpi label={t("coop.kpi_pending_props")} value={proposals.filter((p) => p.status === "pending").length} />
          <Kpi label={t("coop.kpi_interests")} value={interests.length} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none border border-border bg-card h-auto p-0 w-full justify-start">
            <TabsTrigger value="lots" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("coop.tab_lots")} · {lots.length}
            </TabsTrigger>
            <TabsTrigger value="proposals" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("coop.tab_proposals")} · {proposals.length}
            </TabsTrigger>
            <TabsTrigger value="interests" data-testid="coop-tab-samples" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("coop.tab_samples")} · {interests.length}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lots" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("coop.col_lot")}</th>
                    <th className="text-left p-3">{t("coop.col_type")}</th>
                    <th className="text-right p-3">{t("coop.col_bags")}</th>
                    <th className="text-right p-3">{t("coop.col_price")}</th>
                    <th className="text-center p-3">{t("coop.col_auto")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                    <th className="text-right p-3">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l) => (
                    <tr key={l.id} className="border-b border-border/60 hover:bg-secondary/20">
                      <td className="p-3 max-w-[280px]">
                        <div className="font-medium truncate">{l.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{l.farm_name} · {l.city}</div>
                      </td>
                      <td className="p-3"><span className="font-mono-data text-xs uppercase">{tData("coffee", l.coffee_type)}</span></td>
                      <td className="p-3 text-right font-mono-data">{l.bags_quantity}</td>
                      <td className="p-3 text-right font-mono-data text-accent">{formatPrice(l.price_per_bag, l.currency)}</td>
                      <td className="p-3 text-center">
                        {l.auto_pricing_enabled ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-500 font-mono-data">
                            <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-pulse" /> ON
                          </span>
                        ) : <span className="text-xs text-muted-foreground font-mono-data">OFF</span>}
                      </td>
                      <td className="p-3"><StatusBadge status={l.status} /></td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {l.auto_pricing_enabled && (
                            <Button size="icon" variant="ghost" onClick={() => recalc(l.id)} className="h-8 w-8 rounded-none">
                              <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button data-testid={KAFFA.coopLotEdit} size="icon" variant="ghost" onClick={() => startEdit(l)} className="h-8 w-8 rounded-none">
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button data-testid={KAFFA.coopLotPause} size="icon" variant="ghost" onClick={() => pauseLot(l)} className="h-8 w-8 rounded-none">
                            <Pause className="h-3.5 w-3.5" />
                          </Button>
                          <Button data-testid={KAFFA.coopLotDelete} size="icon" variant="ghost" onClick={() => deleteLot(l.id)} className="h-8 w-8 rounded-none text-destructive hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="proposals" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("coop.col_buyer")}</th>
                    <th className="text-left p-3">{t("coop.col_lot")}</th>
                    <th className="text-right p-3">{t("buyer.col_proposed_price")}</th>
                    <th className="text-right p-3">{t("coop.col_bags")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                    <th className="text-right p-3">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t("coop.no_proposals_received")}</td></tr>
                  ) : proposals.map((p) => (
                    <tr key={p.id} className="border-b border-border/60 hover:bg-secondary/20">
                      <td className="p-3">
                        <div className="font-medium">{p.buyer_name}</div>
                        <div className="text-xs text-muted-foreground">{p.buyer_country}</div>
                      </td>
                      <td className="p-3 truncate max-w-[200px]">{p.lot_name}</td>
                      <td className="p-3 text-right font-mono-data text-accent">{formatPrice(p.proposed_price_per_bag, p.currency)}</td>
                      <td className="p-3 text-right font-mono-data">{p.bags_quantity}</td>
                      <td className="p-3"><StatusBadge status={p.status} /></td>
                      <td className="p-3 text-right">
                        {p.status === "pending" && (
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={KAFFA.coopProposalAccept} size="sm" onClick={() => respondProposal(p.id, "accepted")} className="h-7 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-wider">
                              {t("coop.accept_btn")}
                            </Button>
                            <Button data-testid={KAFFA.coopProposalReject} size="sm" variant="outline" onClick={() => respondProposal(p.id, "rejected")} className="h-7 rounded-none text-xs uppercase tracking-wider">
                              {t("coop.reject_btn")}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="interests" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("coop.col_lot")}</th>
                    <th className="text-right p-3">{t("coop.col_bags")}</th>
                    <th className="text-left p-3">{t("samples.col_shipping")}</th>
                    <th className="text-left p-3">{t("samples.col_tracking")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                    <th className="text-left p-3">{t("common.date")}</th>
                    <th className="text-right p-3">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {interests.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{t("coop.no_samples")}</td></tr>
                  ) : interests.map((i) => (
                    <tr key={i.id} data-testid={`coop-sample-row-${i.id}`} className="border-b border-border/60 hover:bg-secondary/20 align-top">
                      <td className="p-3 text-xs">
                        <div className="font-medium">{i.lot_name || `#${i.lot_id?.slice(-6)}`}</div>
                        {i.buyer_name && <div className="text-muted-foreground">{i.buyer_name} · {i.buyer_country}</div>}
                      </td>
                      <td className="p-3 text-right font-mono-data">{i.bags_quantity}</td>
                      <td className="p-3 text-xs">
                        {i.shipping_address ? (
                          <div className="leading-snug">
                            {i.shipping_recipient && <div className="font-medium">{i.shipping_recipient}</div>}
                            <div className="text-muted-foreground">
                              {i.shipping_address}
                              {i.shipping_city ? `, ${i.shipping_city}` : ""}
                              {i.shipping_state ? ` · ${i.shipping_state}` : ""}
                            </div>
                            <div className="text-muted-foreground">{i.shipping_country} {i.shipping_postal_code ? `· ${i.shipping_postal_code}` : ""}</div>
                            {i.shipping_phone && <div className="text-muted-foreground/80">{i.shipping_phone}</div>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{t("samples.address_profile")}</span>
                        )}
                      </td>
                      <td className="p-3 text-xs font-mono-data">
                        {i.tracking_number ? (
                          <>
                            <div data-testid={`coop-sample-${i.id}-tracking`}>{i.tracking_number}</div>
                            {i.tracking_carrier && <div className="text-muted-foreground/70 mt-0.5">{i.tracking_carrier}</div>}
                          </>
                        ) : (
                          <span className="text-muted-foreground/60">{t("samples.tracking_pending")}</span>
                        )}
                      </td>
                      <td className="p-3"><StatusBadge status={i.status} /></td>
                      <td className="p-3 text-muted-foreground text-xs">{formatDate(i.created_at)}</td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`coop-sample-manage-${i.id}`}
                          onClick={() => { setManageSample(i); setManageOpen(true); }}
                          className="h-7 rounded-none text-[10px] uppercase tracking-wider"
                        >
                          {t("samples.manage_btn")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <SampleManageDialog
          key={manageSample?.id || "none"}
          sample={manageSample}
          open={manageOpen}
          onOpenChange={(o) => { setManageOpen(o); if (!o) setManageSample(null); }}
          onSaved={reload}
        />

        {/* Lot form */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-none sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">
                {editingId ? t("coop.edit_lot") : t("coop.create_lot")}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label={t("coop.field_name")} className="sm:col-span-2">
                <Input data-testid={KAFFA.lotFormName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("coop.col_type")}>
                <Select value={form.coffee_type} onValueChange={(v) => setForm({ ...form, coffee_type: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{COFFEE_TYPES.map((c) => <SelectItem key={c} value={c}>{tData("coffee", c)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("coop.field_quality")}>
                <Select value={form.quality_grade} onValueChange={(v) => setForm({ ...form, quality_grade: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{QUALITIES.map((c) => <SelectItem key={c} value={c}>{tData("quality", c)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("coop.col_bags")}>
                <Input data-testid={KAFFA.lotFormBags} type="number" value={form.bags_quantity} onChange={(e) => setForm({ ...form, bags_quantity: Number(e.target.value) })} className="rounded-none" />
              </Field>
              <Field label={t("lot.price_label")}>
                <Input data-testid={KAFFA.lotFormPrice} type="number" step="0.01" value={form.price_per_bag} onChange={(e) => setForm({ ...form, price_per_bag: Number(e.target.value) })} className="rounded-none" disabled={form.auto_pricing_enabled} />
              </Field>
              <Field label={t("coop.field_currency")}>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="BRL">BRL</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label={t("coop.field_sca")}>
                <Input type="number" step="0.1" value={form.sca_score} onChange={(e) => setForm({ ...form, sca_score: Number(e.target.value) })} className="rounded-none" />
              </Field>
              <Field label={t("common.process")}>
                <Select value={form.process} onValueChange={(v) => setForm({ ...form, process: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROCESSES.map((c) => <SelectItem key={c} value={c}>{tData("process", c)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("lot.sieve")}>
                <Select value={form.sieve} onValueChange={(v) => setForm({ ...form, sieve: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIEVES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={`${t("lot.altitude")} (m)`}>
                <Input type="number" value={form.altitude_m} onChange={(e) => setForm({ ...form, altitude_m: Number(e.target.value) })} className="rounded-none" />
              </Field>
              <Field label={t("lot.crop")}>
                <Input value={form.crop_year} onChange={(e) => setForm({ ...form, crop_year: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={`${t("lot.humidity")} (%)`}>
                <Input type="number" step="0.1" value={form.humidity_pct} onChange={(e) => setForm({ ...form, humidity_pct: Number(e.target.value) })} className="rounded-none" />
              </Field>
              <Field label={t("lot.blend")}>
                <Input value={form.blend} onChange={(e) => setForm({ ...form, blend: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("coop.field_farm")}>
                <Input value={form.farm_name} onChange={(e) => setForm({ ...form, farm_name: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("coop.field_city")}>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("coop.field_state")}>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("common.region")}>
                <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("coop.field_port")}>
                <Input value={form.fob_port} onChange={(e) => setForm({ ...form, fob_port: e.target.value })} className="rounded-none" />
              </Field>
              <Field label={t("coop.field_images")} className="sm:col-span-2">
                <Input value={form.images.join(", ")} onChange={(e) => setForm({ ...form, images: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-none" placeholder="https://..." />
              </Field>
              <Field label={t("coop.field_certs")} className="sm:col-span-2">
                <Input value={form.certifications.join(", ")} onChange={(e) => setForm({ ...form, certifications: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-none" placeholder="Fair Trade, Rainforest Alliance" />
              </Field>
              <Field label={t("coop.field_notes")} className="sm:col-span-2">
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" />
              </Field>

              {/* Auto-pricing */}
              <div className="sm:col-span-2 border border-accent/30 bg-accent/5 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-lg tracking-tight flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-accent" /> {t("coop.auto_title")}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("coop.auto_desc")}
                    </p>
                  </div>
                  <Switch
                    data-testid={KAFFA.coopAutoPriceToggle}
                    checked={form.auto_pricing_enabled}
                    onCheckedChange={(v) => setForm({ ...form, auto_pricing_enabled: v })}
                  />
                </div>
                {form.auto_pricing_enabled && (
                  <div className="mt-3">
                    <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{t("coop.margin")}</Label>
                    <Input
                      data-testid={KAFFA.coopMarginInput}
                      type="number" step="0.5" value={form.margin_pct}
                      onChange={(e) => setForm({ ...form, margin_pct: Number(e.target.value) })}
                      className="mt-1.5 h-10 rounded-none w-40"
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={() => setOpen(false)} variant="ghost" className="rounded-none">{t("common.cancel")}</Button>
              <Button data-testid={KAFFA.lotFormSubmit} onClick={submitLot} className="rounded-none bg-primary hover:bg-accent hover:text-accent-foreground">
                {editingId ? t("coop.update") : t("coop.publish")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Kpi({ label, value }) {
  return (
    <div className="bg-card p-5">
      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono-data text-3xl tracking-tight mt-2 text-foreground">{value}</div>
    </div>
  );
}
